/*
  Measures one announcement, fills the bar with enough copies to cover it, and
  hands the CSS a travel distance (one group width) plus a duration derived from
  the speed set in the theme editor. Re-runs when the bar is resized or when the
  chosen webfont finishes loading, since either changes the measurement.

  The measured width is capped at the viewport width. If an ancestor ever
  becomes shrink-to-fit (body-scroll-lock sets `position: fixed` on <body> on
  iOS, which Shopify's preview bar uses), the bar's own clones would otherwise
  feed back into its measured width: every render adds a clone, the bar grows,
  the ResizeObserver fires, and the page locks up while <body> stretches to
  hundreds of thousands of pixels.

  The animation is paused while the bar is scrolled out of view so an
  off-screen marquee costs nothing.
*/
const MAX_COPIES = 40;

class AnnouncementMarquee extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.announcement-bar__track');
    this.group = this.querySelector('.announcement-bar__group');
    this.lastWidth = null;
    this.frame = null;
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    if (!this.track || !this.group) return;

    this.render();

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', this.onResize);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.lastWidth = null;
        this.render();
      });
    }

    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver((entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        this.toggleAttribute('data-marquee-paused', !visible);
      });
      this.visibilityObserver.observe(this);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    window.removeEventListener('resize', this.onResize);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  onResize() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.render());
  }

  render() {
    const viewportWidth = document.documentElement.clientWidth || Infinity;
    const containerWidth = Math.min(this.getBoundingClientRect().width, viewportWidth);
    if (!containerWidth || containerWidth === this.lastWidth) return;
    this.lastWidth = containerWidth;

    this.track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());

    const groupWidth = this.group.getBoundingClientRect().width;
    if (!groupWidth) return;

    const speed = parseFloat(this.dataset.speed) || 48;
    // Hard ceiling as a second guard against runaway cloning.
    const copies = Math.min(Math.ceil(containerWidth / groupWidth) + 1, MAX_COPIES);
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < copies; i++) {
      const clone = this.group.cloneNode(true);
      clone.setAttribute('data-marquee-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      // Repeats are decoration: keep them out of the tab order.
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      fragment.appendChild(clone);
    }

    this.track.appendChild(fragment);
    this.style.setProperty('--announcement-marquee-distance', `${groupWidth}px`);
    this.style.setProperty('--announcement-marquee-duration', `${groupWidth / speed}s`);
    this.setAttribute('data-marquee-ready', '');
  }
}

if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', AnnouncementMarquee);
}
